import tinycolor from "tinycolor2";
import {
  AppDispatch,
  initialColor,
  mixActions,
  useAppDispatch,
} from "../../../store";
import { MixState } from "../store/MixReducer";
import { Message, messageStore } from "./messages";
import { Icons } from "../../../theme/icons";
import { Body } from "../../../components/components";

export const checkContinueAllowed = (
  messages: Message[],
  message: Message,
  mix: MixState
): boolean => {
  const id = message.id;

  if (id) {
    // dependency in id
    switch (id) {
      case "generator-picker":
        return mix.generator !== initialColor; // wait for Gemeinsame Farbe selected
      case "pick-secrets":
        return (
          mix.alice.secret !== initialColor && mix.bob.secret !== initialColor
        );
      case "pick-secret-bob":
        return mix.bob.secret !== initialColor;
      case "show-mix-button-alice":
        return mix.alice.hasMixed;
      case "show-mix-button-bob":
        return mix.bob.hasMixed;
      case "show-result-alice":
        return mix.alice.hasResult;
      case "show-result-bob":
        return mix.bob.hasResult;
      case "select-eve-knowledge":
        return mix.msgProgress.eveKnowledgeGuessedRight;
      case "eve-bruteforces":
        return mix.msgProgress.eveFailedBruteforce;
      case "blatt-1-popup":
      case "blatt-2-popup":
        return mix.msgProgress.popup === null;
      default:
        return true;
    }
  }
  return mix.msgProgress.visibleMsgCount < messages.length;
};

export const isSameSender = (mix: MixState, index: number) => {
  const dispatch = useAppDispatch();
  const messages = messageStore(mix, dispatch);
  if (index === 0) return false;
  return messages[index].target === messages[index - 1].target;
};

export const opac = (condition: boolean): number => {
  if (condition) return 1;
  return 0;
};

const blatt1popup = {
  title: "Wait... Warum hat das funktioniert?",
  icon: <Icons.HandBackLeft size="huge" />,
  children: (
    <Body
      text="Alice & Bob haben sich nun ihren Schlüssel gemischt. Warum ist bei beiden unten genau die gleiche Farbe rausgekommen? Bearbeite Aufgabe 1 auf dem Handout, um dies herauszufinden :D."
      style={{ width: "80%", textAlign: "center" }}
    />
  ),
};

const blatt2popup = {
  title: "Bruh, das war wohl nichts für Eve...",
  icon: <Icons.ChatRemove size="huge" />,
  children: (
    <Body
      text="Eve konnte die geheime Farbe von Alice leider nicht erraten. Aber gibt es dazu andere Möglichkeiten? Bearbeite Aufgabe 2 auf dem Handout und finde es heraus!"
      style={{ width: "80%", textAlign: "center" }}
    />
  ),
};

export const updateMessageUI = (dispatch: AppDispatch, newMsgID?: string) => {
  if (newMsgID) {
    // associated actions defined here
    switch (newMsgID) {
      case "pick-secrets":
        dispatch(mixActions.updateProgress({ secretsShown: true }));
        break;
      case "alice-has-generator":
        dispatch(mixActions.showGenerator("alice"));
        break;
      case "bob-has-generator":
        dispatch(mixActions.showGenerator("bob"));
        break;
      case "show-mix-button-alice":
      case "show-mix-button-bob": // redundant so failsafe if order changed in future
        dispatch(mixActions.updateProgress({ mixBtnsShown: true }));
        break;
      case "bob-has-other-mix":
        dispatch(mixActions.send("alice"));
        dispatch(mixActions.hasOtherMix("bob"));
        break;
      case "alice-has-other-mix":
        dispatch(mixActions.send("bob"));
        dispatch(mixActions.hasOtherMix("alice"));
        break;
      case "show-result-alice":
      case "show-result-bob":
        dispatch(mixActions.updateProgress({ resultsShown: true }));
        break;
      case "blatt-1-popup":
        dispatch(mixActions.showPopup(blatt1popup));
        break;
      case "blatt-2-popup":
        dispatch(mixActions.showPopup(blatt2popup));
      default:
        break;
    }
  }
  return;
};

export const colorPercentDiff = (color1: string, color2: string): number => {
  const rgb1 = tinycolor(color1).toRgb();
  const rgb2 = tinycolor(color2).toRgb();

  const diffs = {
    r: rgb1.r - rgb2.r,
    g: rgb1.g - rgb2.g,
    b: rgb1.b - rgb2.b,
  };

  const distance = Math.sqrt(diffs.r ** 2 + diffs.g ** 2 + diffs.b ** 2);
  const maxDistance = Math.sqrt(3 * 255 ** 2); // maximaler RGB Farbraum: √(255² + 255² + 255²)

  const percent = (distance / maxDistance) * 100;
  return Math.round(percent * 100) / 100; // auf 2 Nachkommastellen gerundet
};
